import { Action, notEmpty } from 'utils'
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs'
import { ofType, StateObservable } from 'redux-observable'
import { delay, filter, map, mergeMap, take, tap } from 'rxjs/operators'
import {
  ClientActionTypes,
  ClientMessageEventPayloadUnion,
  DealActions,
  EntitySnapshotEndAction,
  EntityType,
  EntityTypeEnum,
  EntityUpdateAction,
  NegotiationActions,
  OrderActions,
  ServerActionTypes,
  SubscriptionIdentifier
} from 'api'
import { EntityUpdateTypeFor, SubscriptionFilterConfig, updateWithStateFilters } from 'model'
import { GlobalState } from 'store'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createUserByUsernameGetterSelector } from 'selectors/userSelectors'
import { BTQAction } from 'epics'
import { AuditActions } from 'api/AuditActions'
import { AxeActions } from 'api/AxeAction'
import { isEqual } from 'lodash'
import { createEntitySubscriptionFiltersSelector } from 'selectors/selectionSelectors'

export function ofEntityUpdateType<TEntityType extends EntityType>(entityType: TEntityType, includeSnapshot: boolean = false) {
  return function filterByEntity(action$: Observable<BTQAction>): Observable<EntityUpdateTypeFor<TEntityType>> {
    return action$.pipe(
      ofType<BTQAction, ServerActionTypes.ENTITY_UPDATE, EntityUpdateAction>(ServerActionTypes.ENTITY_UPDATE),
      filter(action => includeSnapshot || !action.payload.isSnapshot),
      filter(action => action.payload.update.entityType === entityType),
      map(action => action.payload.update as EntityUpdateTypeFor<TEntityType>)
    )
  }
}

export const ofClientEventType = <TStateMachineMessage extends ClientMessageEventPayloadUnion>(
  eventType: TStateMachineMessage['eventType']
) => {
  return (action$: Observable<BTQAction>) => {
    return action$.pipe(
      ofType<BTQAction, ClientActionTypes.EVENT_MESSAGE, Action<ClientActionTypes.EVENT_MESSAGE, TStateMachineMessage>>(
        ClientActionTypes.EVENT_MESSAGE
      ),
      filter(action => action.payload.eventType === eventType)
    )
  }
}

export function ofEntitySnapshotEndType<TEntityType extends EntityType>(entityType: TEntityType) {
  return function filterByEntity(action$: Observable<BTQAction>) {
    return action$.pipe(
      ofType<BTQAction, ServerActionTypes.ENTITY_SNAPSHOT_END, EntitySnapshotEndAction>(ServerActionTypes.ENTITY_SNAPSHOT_END),
      filter(action => action.payload.entityType === entityType),
      take(1)
    )
  }
}

export function getIsUpdatingUserFromDifferentOrganisation(lastUpdatedBy: string, state: GlobalState) {
  const authorisedUser = createAuthorisedUserSelector()(state)
  const lastUpdatedUser = createUserByUsernameGetterSelector(state)(lastUpdatedBy)
  if (lastUpdatedUser) {
    return authorisedUser.organisationId !== lastUpdatedUser.organisationId
  } else {
    // If we can't find the last updated user from the user name, it should mean that it's from a different org
    return notEmpty(lastUpdatedBy)
  }
}

export const rateLimit = (tokenCount: number, tokenReturnInterval: number) => {
  const tokenCount$ = new BehaviorSubject(tokenCount)
  const tokenAvailable$ = tokenCount$.pipe(
    filter(() => tokenCount$.getValue() > 0),
    map(() => undefined)
  )
  const takeToken = () => tokenCount$.next(tokenCount$.getValue() - 1)
  const returnToken = () => tokenCount$.next(tokenCount$.getValue() + 1)

  return <T>(source$: Observable<T>) =>
    source$.pipe(
      mergeMap(v =>
        tokenAvailable$.pipe(
          take(1),
          map(() =>
            of(1)
              .pipe(tap(takeToken), delay(tokenReturnInterval), tap(returnToken))
              .subscribe()
          ),
          map(() => v)
        )
      )
    )
}

export function updateSubscriptions$({
  filterConfig,
  state$
}: {
  filterConfig: SubscriptionFilterConfig
  state$: StateObservable<GlobalState>
}): Observable<BTQAction> {
  const activeOrdersFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.ORDER,
    () => SubscriptionIdentifier.STAGING
  )(state$.value, {})

  const ordersFilters = updateWithStateFilters(activeOrdersFilters, SubscriptionIdentifier.STAGING, filterConfig)

  const activeInitiatedNegotiationFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INITIATED
  )(state$.value, {})

  const activeInitiatedGridViewNegotiationFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INITIATED_GRID_VIEW
  )(state$.value, {})

  const initiatedNegotiationFilters = updateWithStateFilters(
    activeInitiatedNegotiationFilters,
    SubscriptionIdentifier.INITIATED,
    filterConfig
  )

  const initiatedGridViewNegotiationFilters = updateWithStateFilters(
    activeInitiatedGridViewNegotiationFilters,
    SubscriptionIdentifier.INITIATED_GRID_VIEW,
    filterConfig
  )

  const activeIncomingNegotiationFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INCOMING
  )(state$.value, {})
  const incomingNegotiationFilters = updateWithStateFilters(activeIncomingNegotiationFilters, SubscriptionIdentifier.INCOMING, filterConfig)

  const activeInitiatedTerminatedNegotiationFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION
  )(state$.value, {})
  const initiatedTerminatedNegotiationFilters = updateWithStateFilters(
    activeInitiatedTerminatedNegotiationFilters,
    SubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION,
    filterConfig
  )

  const activeIncomingTerminatedNegotiationFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION
  )(state$.value, {})
  const incomingTerminatedNegotiationFilters = updateWithStateFilters(
    activeIncomingTerminatedNegotiationFilters,
    SubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION,
    filterConfig
  )

  const activeHistoricalNegotiationFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION_HISTORY,
    () => SubscriptionIdentifier.HISTORICAL
  )(state$.value, {})
  const historicalNegotiationFilters = updateWithStateFilters(
    activeHistoricalNegotiationFilters,
    SubscriptionIdentifier.HISTORICAL,
    filterConfig
  )

  const activeNegotiationHistoryFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.NEGOTIATION_HISTORY,
    () => SubscriptionIdentifier.LATEST
  )(state$.value, {})
  const negotiationHistoryFilters = updateWithStateFilters(activeNegotiationHistoryFilters, SubscriptionIdentifier.LATEST, filterConfig)

  const activeAxeInitiatorFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.AXE,
    () => SubscriptionIdentifier.AXE_TODAY_INITIATOR
  )(state$.value, {})
  const axeInitiatorFilters = updateWithStateFilters(activeAxeInitiatorFilters, SubscriptionIdentifier.AXE_TODAY_INITIATOR, filterConfig)

  const activeAxeResponderFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.AXE,
    () => SubscriptionIdentifier.AXE_TODAY_RESPONDER
  )(state$.value, {})
  const axeResponderFilters = updateWithStateFilters(activeAxeResponderFilters, SubscriptionIdentifier.AXE_TODAY_RESPONDER, filterConfig)

  const activeDealFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.DEAL,
    () => SubscriptionIdentifier.BLOTTER
  )(state$.value, {})
  const dealFilters = updateWithStateFilters(activeDealFilters, SubscriptionIdentifier.BLOTTER, filterConfig)

  const activeOpenRepoFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.DEAL,
    () => SubscriptionIdentifier.OPEN_REPO
  )(state$.value, {})
  const openRepoFilters = updateWithStateFilters(activeOpenRepoFilters, SubscriptionIdentifier.OPEN_REPO, filterConfig)

  const activeRollsFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.DEAL,
    () => SubscriptionIdentifier.ROLLS
  )(state$.value, {})
  const rollsFilters = updateWithStateFilters(activeRollsFilters, SubscriptionIdentifier.ROLLS, filterConfig)

  const activeStructuredFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.DEAL,
    () => SubscriptionIdentifier.STRUCTURED
  )(state$.value, {})
  const structuredFilters = updateWithStateFilters(activeStructuredFilters, SubscriptionIdentifier.STRUCTURED, filterConfig)

  const activeMidTradeEventHistoryFilters = createEntitySubscriptionFiltersSelector(
    () => EntityTypeEnum.DEAL_HISTORY,
    () => SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS
  )(state$.value, {})
  const midTradeEventHistoryFilters = updateWithStateFilters(
    activeMidTradeEventHistoryFilters,
    SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS,
    filterConfig
  )

  const actions = [
    ...(isEqual(activeOrdersFilters, ordersFilters) ? [] : [OrderActions.pageSubscribe(SubscriptionIdentifier.STAGING, ordersFilters)]),
    ...(isEqual(activeInitiatedNegotiationFilters, initiatedNegotiationFilters)
      ? []
      : [NegotiationActions.pageSubscribe(SubscriptionIdentifier.INITIATED, initiatedNegotiationFilters)]),
    ...(isEqual(activeInitiatedGridViewNegotiationFilters, initiatedGridViewNegotiationFilters)
      ? []
      : [NegotiationActions.pageSubscribe(SubscriptionIdentifier.INITIATED_GRID_VIEW, initiatedGridViewNegotiationFilters)]),
    ...(isEqual(activeIncomingNegotiationFilters, incomingNegotiationFilters)
      ? []
      : [NegotiationActions.pageSubscribe(SubscriptionIdentifier.INCOMING, incomingNegotiationFilters)]),

    ...(isEqual(activeAxeInitiatorFilters, axeInitiatorFilters)
      ? []
      : [AxeActions.pageSubscribe(SubscriptionIdentifier.AXE_TODAY_INITIATOR, axeInitiatorFilters)]),

    ...(isEqual(activeAxeResponderFilters, axeResponderFilters)
      ? []
      : [AxeActions.pageSubscribe(SubscriptionIdentifier.AXE_TODAY_RESPONDER, axeResponderFilters)]),

    ...(isEqual(activeInitiatedTerminatedNegotiationFilters, initiatedTerminatedNegotiationFilters)
      ? []
      : [
          NegotiationActions.liveOnlySubscribe(
            SubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION,
            initiatedTerminatedNegotiationFilters
          )
        ]),
    ...(isEqual(activeIncomingTerminatedNegotiationFilters, incomingTerminatedNegotiationFilters)
      ? []
      : [
          NegotiationActions.liveOnlySubscribe(
            SubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION,
            incomingTerminatedNegotiationFilters
          )
        ]),
    ...(isEqual(activeHistoricalNegotiationFilters, historicalNegotiationFilters)
      ? []
      : [AuditActions.pageSubscribe(SubscriptionIdentifier.HISTORICAL, historicalNegotiationFilters)]),
    ...(isEqual(activeNegotiationHistoryFilters, negotiationHistoryFilters)
      ? []
      : [AuditActions.pageSubscribe(SubscriptionIdentifier.LATEST, negotiationHistoryFilters)]),
    ...(isEqual(activeDealFilters, dealFilters) ? [] : [DealActions.pageSubscribe(SubscriptionIdentifier.BLOTTER, dealFilters)]),
    ...(isEqual(activeOpenRepoFilters, openRepoFilters)
      ? []
      : [DealActions.pageSubscribe(SubscriptionIdentifier.OPEN_REPO, openRepoFilters)]),
    ...(isEqual(activeRollsFilters, rollsFilters) ? [] : [DealActions.pageSubscribe(SubscriptionIdentifier.ROLLS, rollsFilters)]),
    ...(isEqual(activeStructuredFilters, structuredFilters)
      ? []
      : [DealActions.pageSubscribe(SubscriptionIdentifier.STRUCTURED, structuredFilters)]),
    ...(isEqual(activeMidTradeEventHistoryFilters, midTradeEventHistoryFilters)
      ? []
      : [DealActions.pageHistorySubscribe(SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS, midTradeEventHistoryFilters)])
  ]

  return actions.length > 0 ? of(...actions) : EMPTY
}
